import React, { Fragment, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { navigate } from 'gatsby';
import { groupBy, isEqual, isEmpty } from 'lodash-es';
import { Button, Input, List } from 'chaoskit/src/components';
import { styles } from 'chaoskit/src/helpers/styles';
import useUpdateEffect from 'react-use/lib/useUpdateEffect';
import {
  InstantSearch,
  Configure,
  Highlight,
  connectAutoComplete,
} from 'react-instantsearch-dom';
import algoliaSearch from 'algoliasearch/lite';

import { Icon, Link } from '../../components';
import { config } from '../../helpers/config';
import useDebounce from '../../hooks/useDebounce';
import logoMascot from '../../assets/media/logo-mascot.svg';

const searchClient = algoliaSearch(
  config.algolia.applicationId,
  config.algolia.apiKey
);

// eslint-disable-next-line no-unused-vars
const groupSearchResults = hits => {
  if (!hits.length) return [];

  // Group results by section
  return Object.entries(groupBy(hits, x => x.section)).map(([key, value]) => ({
    section: key,
    items: value,
  }));
};

const SearchResults = ({ hitGroups, activeHit, resetActiveHit }) => {
  const searchResultsContainerRef = useRef();
  const activeHitRef = useRef();

  const searchResultsContainerClasses = cx(
    'docs__header-search-resultsContainer',
    {
      'has-results': hitGroups.length,
    }
  );

  useUpdateEffect(() => {
    if (searchResultsContainerRef.current && activeHitRef.current) {
      // Update scroll position in overflow container to `activeHit` offset - the height of our section headers (since they are sticky now)
      searchResultsContainerRef.current.scrollTo({
        top:
          activeHitRef.current.offsetTop - parseInt(styles.height.xsmall, 10),
        behavior: 'smooth',
      });
    } else if (searchResultsContainerRef.current && !activeHitRef.current) {
      // Push scroll position to the top only if we're not already there (from a previously registered activeHit)
      if (searchResultsContainerRef.current.offsetTop > 0) {
        searchResultsContainerRef.current.scrollTo({ top: 0 });
      }
    }
  }, [activeHit]);

  return (
    <div
      className={searchResultsContainerClasses}
      ref={searchResultsContainerRef}
    >
      {hitGroups.length ? (
        <List className="u-mb--remove docs__header-search-results">
          {hitGroups.map(group => (
            <Fragment key={group.section}>
              <li
                className="docs__header-search-results-section"
                style={{ height: styles.height.xsmall }}
              >
                {group.section}
              </li>
              {group.items.map(hit => (
                <li
                  key={hit.slug}
                  ref={isEqual(hit, activeHit) ? activeHitRef : null}
                >
                  <Link
                    to={`${hit.slug}${
                      hit.contentId ? `#${hit.contentId}` : ''
                    }`}
                    className={cx('docs__header-search-results-link', {
                      'is-highlighted': isEqual(hit, activeHit),
                    })}
                    onClick={() => resetActiveHit()}
                  >
                    <div className="docs__header-search-results-title">
                      <Highlight tagName="mark" attribute="title" hit={hit} />
                      <Icon
                        size="small"
                        className="docs__header-search-results-icon"
                        icon="arrow-right"
                      />
                    </div>
                    {hit.excerpt && (
                      <p className="docs__header-search-results-body u-mt--small u-textSmall">
                        <Highlight
                          tagName="mark"
                          attribute="excerpt"
                          hit={hit}
                        />
                      </p>
                    )}
                  </Link>
                </li>
              ))}
            </Fragment>
          ))}
        </List>
      ) : (
        <div className="docs__header-search--noResults">
          <img src={logoMascot} alt="Gremlin" />
          <p className="u-textSmall u-textMuted">No results found.</p>
        </div>
      )}
    </div>
  );
};

SearchResults.propTypes = {
  hitGroups: PropTypes.array,
  activeHit: PropTypes.object,
  resetActiveHit: PropTypes.func.isRequired,
};

const AutoComplete = ({ hits, currentRefinement, refine }) => {
  const [searchValue, setSearchValue] = useState(currentRefinement);
  const [activeHit, setActiveHit] = useState({});

  // Debounce input changes
  const debouncedSearchValue = useDebounce(searchValue, 150);

  useUpdateEffect(() => {
    refine(debouncedSearchValue);
    // Always reset active hit so there's no persistence between new results
    setActiveHit({});
  }, [debouncedSearchValue]);

  const groupedHits = groupSearchResults(hits);
  // Create temp array that flattens results and removes section grouping
  // Doing this since our `groupSearchResults` changes order of our normal `hits` array
  const flattenedHits = groupedHits.reduce(
    (previous, current) => previous.concat(current.items),
    []
  );

  const handleKeyUp = ({ keyCode }) => {
    const up = 38;
    const down = 40;
    const enter = 13;

    // If we have hits and we pressed one of our allowed keyCodes:
    if (hits.length && [up, down, enter].includes(keyCode)) {
      if (keyCode === down && !isEmpty(activeHit)) {
        // Find current active hit
        const position = flattenedHits.findIndex(
          hit => hit.slug === activeHit.slug
        );

        // Set position to either the next one (if possible) or the beginning of the list
        setActiveHit(flattenedHits[position + 1] || flattenedHits[0]);
      } else if (keyCode === down) {
        setActiveHit(flattenedHits[0]);
      } else if (keyCode === up && !isEmpty(activeHit)) {
        // Find current active hit
        const position = flattenedHits.findIndex(
          hit => hit.slug === activeHit.slug
        );

        // Set position to either the previous one (if possible) or last of the list
        setActiveHit(
          flattenedHits[position - 1] || flattenedHits[flattenedHits.length - 1]
        );
      } else if (keyCode === up) {
        setActiveHit(flattenedHits[flattenedHits.length - 1]);
      } else if (keyCode === enter && !isEmpty(activeHit)) {
        // Reset between clicks
        refine('');
        setSearchValue('');

        navigate(activeHit.slug);
      }
    }
  };

  return (
    <Fragment>
      <Input
        autoComplete="off"
        name="search"
        placeholder="Search Documentation"
        prefixIcon="search"
        value={searchValue}
        onChange={(name, value) => setSearchValue(value)}
        className={cx({ 'has-query': searchValue.length !== 0 })}
        onKeyUp={handleKeyUp}
      />
      {searchValue.length !== 0 && (
        <Button
          size="xsmall"
          onClick={() => {
            setSearchValue('');
            refine('');
          }}
          iconOnly
          title="Reset query"
          className="docs__header-search-resetQueryButton"
        >
          <Icon icon="close" />
        </Button>
      )}

      {currentRefinement && (
        <SearchResults
          hitGroups={groupedHits}
          activeHit={activeHit}
          resetActiveHit={() => refine('')}
        />
      )}
    </Fragment>
  );
};

const CustomAutoComplete = connectAutoComplete(AutoComplete);

AutoComplete.propTypes = {
  hits: PropTypes.array,
  currentRefinement: PropTypes.string,
  refine: PropTypes.func,
};

const DocSearch = () => {
  return (
    <Fragment>
      <div className="docs__header-search-wrapper">
        <InstantSearch indexName="documentation" searchClient={searchClient}>
          <CustomAutoComplete />
          <Configure hitsPerPage={20} />
        </InstantSearch>
      </div>
    </Fragment>
  );
};

export default DocSearch;
